import {
  ArrowClockwiseIcon,
  InfoIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import moment from "moment";

import QRCode from "@alpacahq/qrcode.react";
import { Banner, clsx, Spinner } from "@alpacahq/ui/old";

import { CopyButton } from "~/v2/components/common";
import { DEPOSIT_NETWORK_WARNING_MESSAGE } from "../../const";
import type { TransferCardControllerReturn } from "../TransferCard.controller";

interface DepositProps
  extends Pick<TransferCardControllerReturn, "asset" | "deposit"> {}

export const Deposit = ({ asset, deposit }: DepositProps) => (
  <section className="flex flex-col gap-1">
    <h3 className="m-0 p-0 font-normal text-gray-800 text-sm">
      Deposit Address
    </h3>
    <div
      className={clsx(
        "flex flex-col items-center justify-center gap-2 rounded border",
        deposit.isError
          ? "border-red-300 bg-red-50 p-3"
          : asset && deposit.address
            ? "border-gray-400 p-3"
            : "border-gray-200 p-2",
      )}
    >
      {deposit.isError ? (
        <div className="flex flex-col items-center gap-2 py-1">
          <WarningCircleIcon size={20} className="text-red-500" />
          <p className="m-0 text-center font-medium text-red-600 text-sm">
            Failed to load deposit address
          </p>
          <button
            type="button"
            onClick={deposit.onRetry}
            className="flex items-center gap-1.5 font-medium text-indigo-600 text-xs transition-colors hover:text-indigo-700"
          >
            <ArrowClockwiseIcon size={14} />
            Retry
          </button>
        </div>
      ) : asset && deposit.address ? (
        <>
          <div className="flex items-center justify-center gap-2">
            <QRCode value={deposit.address.address} size={64} />
            <div className="m-0 flex items-center gap-2 rounded border border-gray-400 border-dashed p-2">
              <p className="m-0 break-all font-medium font-mono text-gray-700 text-xs">
                {deposit.address.address}
              </p>
              <CopyButton
                value={deposit.address.address}
                ariaLabel="Copy address"
                className="cursor-copy transition-colors hover:bg-gray-50"
              />
            </div>
          </div>
          {deposit.address?.migrated_at && (
            <div className="flex items-center justify-center gap-2">
              <InfoIcon size={20} />
              This is your new address generated on{" "}
              {moment(deposit.address.migrated_at).format("MM/DD/YYYY")}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="m-0 p-0 font-medium text-gray-400 text-sm">
            Address and QR code will be shown here.
          </p>
          {deposit.isLoading && <Spinner />}
        </>
      )}
    </div>
    {!!asset && !!deposit.address && deposit.accountLabel && (
      <p className="m-0 mt-3 flex items-center justify-between gap-2 p-0 text-xs leading-5">
        <span className="whitespace-nowrap text-gray-700">Deposit account</span>
        <span className="whitespace-nowrap font-medium text-gray-950">
          {deposit.accountLabel}
        </span>
      </p>
    )}
    {deposit.showNetworkWarning && (
      <Banner
        variant="warning"
        icon={<span />}
        message={DEPOSIT_NETWORK_WARNING_MESSAGE}
        className="border border-orange-600 bg-[rgba(254,215,170,0.24)] p-3"
      />
    )}
  </section>
);
